import React, { FC, useEffect, useState } from 'react';
import { Button, FormInstance, Radio, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import styled from 'styled-components';
import Form from '~/components/WizardForm';
import buildNamePath from '~/utils/buildNamePath';
import { useApplicationFormData, useGetApplication } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { getStagePayload } from '~/utils/getStagePayload';
import { Draft } from '~/types/Draft';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';

interface Props {
  form: FormInstance;
}
const n = buildNamePath('additionalInformation');

const fatcaQuestions = [
  {
    id: 'isPublicCompany',
    name: n(['fatcaCrs', 'isPublicCompany']),
    question: 'Is the entity a public listed company or an affiliate of a publicly listed company?',
    // description:
    //   'If Yes, skip the remaining questions, however if No please complete the remaining questions and sign below',

    value: undefined,
  },
  {
    id: 'isGovernmentOwned',
    name: n(['fatcaCrs', 'isGovernment']),
    question: 'Is the entity government-owned?',
    // description:
    //   'If Yes, skip the remaining questions, however if No please complete the remaining questions and sign below',
    value: undefined,
  },
  {
    id: 'taxExemptRetirement',
    name: n(['fatcaCrs', 'isRetirementFund']),
    question: 'Is the entity a tax-exempt retirement fund or trust',
    // description:
    //   'If Yes, skip the remaining questions, however if No please complete the remaining questions and sign below',
    value: undefined,
  },
  {
    id: 'taxExemptCharity',
    name: n(['fatcaCrs', 'isCharity']),
    question: 'Is this entity a tax exempt, not for profit entity (charity)?',
    // description:
    //   'If Yes, skip the remaining questions, however if No please complete the remaining questions and sign below',
    value: undefined,
  },
  {
    id: 'usBased',
    name: n(['fatcaCrs', 'isUSBased']),
    question: 'Is the country of incorporation and/or parent company country of incorporation in the U.S.?',
    value: undefined,
  },
  {
    id: 'nonFinancial',
    name: n(['fatcaCrs', 'isNonFinancial']),
    question:
      'Is the entity an active non-financial entity or a passive non-financial entity established outside U.S.?',
    value: undefined,
  },
  {
    id: 'poaHolderOutsideUSA',
    name: n(['fatcaCrs', 'isPOAHolderOutsideUSA']),
    question: 'Does this entity have POA holder or authorized signatory with a U.S. Mailing address?',
    value: undefined,
  },
  {
    id: 'shareHolderUSBased',
    name: n(['fatcaCrs', 'isShareHolderUSBased']),
    question:
      'Are you aware if any of the shareholders, beneficial owners, directors or partners (with >= 5% ownership) of the company hold a U.S. Passport, hold a U.S. Green card, or U.S. Social security number)?',
    value: undefined,
  },
  {
    id: 'conductOfTradeUSA',
    name: n(['fatcaCrs', 'isConductOfTradeUSA']),
    question: 'Is this entity’s income effectively connected with the conduct of a trade or business in the U.S.?',
    value: undefined,
  },
];

const craQuestions = [
  {
    id: 'establishedInUAE',
    name: n(['fatcaCrs', 'isEstablishedInUAE']),
    question: 'Is the entity established in the UAE?',
    // description: 'If your answer is No , then please complete the Entity CRS form',
    value: undefined,
  },
  {
    id: 'companySanctionedBased',
    name: n(['fatcaCrs', 'isCompanySanctionedBased']),
    question:
      'Is your company owned by any person/entity that is incorporated/based in or resident in a sanctioned country/territory?',
    // description: 'If your answer is No , then please complete the Entity CRS form',
    value: undefined,
  },
  {
    id: 'controllingPersonType',
    name: n(['fatcaCrs', 'isControllingPersonType']),
    question:
      'Does the controlling person(s) profession mentioned under the resident visa match any of the following types “investor”, “real estate”, “housewife” or “partner”?',
    // description:
    //   'If your answer is yes , then the applicable controlling person (shareholders, partner or authorized  signatory) to provide us the latest UAE utility bill as proof of residency or the authorized person who is signing on behalf of the entity to complete the Entity CRS Form along with specifying their residency status.',
    value: undefined,
  },
];

const FatcaCrsSelfDeclarationation: FC<Props> = (props) => {
  const { form } = props;
  const { t } = useTranslation('fillApplication');
  const applicationQuery = useGetApplication();
  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  if (!form) return <Spin size="large" />;

  const renderQuestion = (question: any) => {
    return (
      <>
        <Typography.Title level={5} className="Questionnaire-NoBottomSpace">
          {t(question.question)}
        </Typography.Title>

        {/* <Typography.Paragraph>{question.description}</Typography.Paragraph> */}
        <Form.Item name={question.name}>
          <Radio.Group>
            {question.id === 'usBased' ? (
              <>
                <Radio value>{t('Active')}</Radio>
                <Radio value={false}>{t('Passive')}</Radio>
              </>
            ) : (
              <>
                <Radio value>{t('Yes')}</Radio>
                <Radio value={false}>{t('No')}</Radio>
              </>
            )}
          </Radio.Group>
        </Form.Item>
      </>
    );
  };

  return (
    <FormItemContextProvider value={{}}>
      <Container>
        {fatcaQuestions.map((fatcaQuestion) => renderQuestion(fatcaQuestion))}
        <Typography.Paragraph className="Questionnaire-Asterisk">
          <p>
            {t(
              'Passive Definition: Entities generate > 50% of their income from passive sources such as interest, dividend, income equivalent to interest, rents and royalties, excess of gains over losses from the sale or exchange of property etc.',
            )}
          </p>
          <p>
            {t(
              'Active Definition: Entities generate > 50% of their income from nonpassive sources such as non-investment businesses like trading, manufacturing, or provision of services etc.',
            )}
          </p>
        </Typography.Paragraph>

        {craQuestions.map((craQuestion) => renderQuestion(craQuestion))}
      </Container>
    </FormItemContextProvider>
  );
};

const Container = styled.div`
  .Questionnaire-NoBottomSpace {
    margin-bottom: 0;
  }
  .Questionnaire-Asterisk {
    font-size: 12px;
    line-height: 16px;
  }
`;

export default FatcaCrsSelfDeclarationation;
